import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { ScrollVertical } from "@tento-scroll/vertical/scroll-vertical";

/** The virtualized host an agent transcript scrolls in: a flat list of
 * variable-height rows that mounts only what is visible, and follows the
 * tail while the reader is at the bottom.
 *
 * It knows nothing about records — the caller passes `count` and a
 * `renderAt(index)` that returns the row element, so every surface keeps
 * its own vocabulary. Rows must be flat (one row per item, not one per
 * turn); wrapping a whole turn in a single row defeats the point.
 *
 * Height is measured, never declared: each cell holds an
 * absolutely-positioned wrapper whose natural height is observed and fed
 * back to the scroll engine, so rows may reflow (wrapping text, expanding
 * blocks) without the caller predicting anything. */
@component({ name: "std-agent-transcript" })
export class AgentTranscript extends LonaWebComponent {
  private $root: HTMLElement = this.$("root");

  private $scroll: Option<ScrollVertical<HTMLElement>>;
  private renderAt: Option<(index: number) => HTMLElement>;
  private count: number = 0;
  private revision: Option<string>;
  private estimatedRowHeight: number = AgentTranscript.DEFAULT_ROW_HEIGHT;

  /** index → measured height. Survives recycling; cleared on re-bind. */
  private readonly measured = new Map<number, number>();
  private readonly cellIndex = new WeakMap<HTMLElement, number>();
  private observer: Option<ResizeObserver>;

  private following: boolean = true;
  private lastTop: number = 0;
  private ready: boolean = false;

  static makeWith(props: AgentTranscript.Props): AgentTranscript {
    return (AgentTranscript.make() as AgentTranscript).bind(props);
  }

  bind(props: AgentTranscript.Props): this {
    this.renderAt = props.renderAt;
    this.estimatedRowHeight = props.estimatedRowHeight ??
      AgentTranscript.DEFAULT_ROW_HEIGHT;
    const previous = this.count;
    const revisionChanged = this.revision !== props.revision;
    this.count = props.count;
    this.revision = props.revision;

    if (!this.$scroll) {
      this.mount();
      this.syncEmpty();
      return this;
    }
    if (this.count !== previous || revisionChanged) {
      if (revisionChanged) this.measured.clear();
      this.syncEmpty();
      if (this.count > 0) {
        this.$scroll.updateMaxIndex(this.count - 1);
        // Re-render the mounted cells against the new item list.
        this.$scroll.invalidateHeight(0, !this.following);
        if (this.following) this.scrollToEnd();
      }
    }
    return this;
  }

  /** An empty transcript owns no phantom row. `maxIndex: 0` still means ONE
   * cell to the engine, and onBind's range guard returns without clearing a
   * wrap it will not fill — so a rebind from one row to zero would leave the
   * previous turn visible in a transcript that claims to be empty. At count
   * zero the host is hidden outright (the stale cell can neither paint nor
   * claim scroll height) and restored the moment rows exist again; the next
   * positive bind's updateMaxIndex + invalidation rebuilds every cell. */
  private syncEmpty(): void {
    if (this.$scroll) {
      this.$scroll.style.display = this.count === 0 ? "none" : "";
    }
  }

  /** Jump to the newest row. Called on bind and whenever a measurement
   * lands while the reader is following the tail. */
  scrollToEnd(): void {
    if (!this.ready || !this.$scroll || this.count === 0) return;
    // Tail-follow: move the view, keep the map and the bound rows.
    this.$scroll.scrollEngine.bindIndex(this.count - 1, {
      kind: "reveal",
      at: {
        position: "end",
        scrollBehavior: "instant",
        useNativeScrollTo: true,
      },
      viewportChangeSource: "bind",
    });
  }

  /** True while the reader sits at the tail — scrolling up releases it. */
  get isFollowing(): boolean {
    return this.following;
  }

  private mount(): void {
    // HTMLElement, not HTMLDivElement: the base make() hands back the
    // former, and narrowing it needed a cast tsc rejects outright. The
    // cells this host builds are divs, but nothing here reads a div-only
    // member, so the wider parameter is the honest one.
    const $scroll = ScrollVertical.make() as ScrollVertical<HTMLElement>;
    this.$scroll = $scroll;
    this.observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const $wrap = entry.target as HTMLElement;
        const $cell = $wrap.parentElement;
        if ($cell == null) continue;
        // The BORDER box, never contentRect. The row gap is padding on the
        // wrap — inside the measured box by design, see `.wrap` below — and
        // contentRect is the content box, which excludes it: reading it
        // stored every row one gap short of what onBind's offsetHeight
        // reported, and the shorter number always landed last. Both paths
        // must name the same box or the later one silently wins.
        const height = entry.borderBoxSize?.[0]?.blockSize ??
          $wrap.offsetHeight;
        this.report($cell, height);
      }
    });

    $scroll.initialize({
      onCreate: () => {
        const $cell = document.createElement("div");
        $cell.className = "cell";
        const $wrap = document.createElement("div");
        $wrap.className = "wrap";
        $cell.append($wrap);
        this.observer?.observe($wrap, { box: "border-box" });
        return $cell;
      },
      onBind: ($cell, index, _height, resizeOnly) => {
        if (resizeOnly) return;
        this.cellIndex.set($cell, index);
        if (index < 0 || index >= this.count || this.renderAt == null) return;
        const $wrap = $cell.firstElementChild as HTMLElement;
        // Recycling unobserved this wrap; a full bind is where it rejoins
        // the pipeline. Re-observing an already-observed wrap is a no-op.
        this.observer?.observe($wrap, { box: "border-box" });
        $wrap.replaceChildren(this.renderAt(index));
        const known = this.measured.get(index);
        if (known != null) $cell.style.height = `${known}px`;
        this.report($cell, $wrap.offsetHeight);
      },
      onRecycle: ($cell) => {
        const $wrap = $cell.firstElementChild as HTMLElement | null;
        if ($wrap == null) return;
        // Recycling must stop the measurement pipeline, not just empty the
        // cell: the observer outlives the content, and `cellIndex` still
        // names the row this cell LAST held — so the emptied wrap's collapse
        // to its bare padding was reported as that row's height, and merely
        // scrolling past a row destroyed its measurement. Unobserve stops
        // the next delivery; the `recycled` guard in `report` swallows the
        // one already in flight.
        this.observer?.unobserve($wrap);
        $wrap.replaceChildren();
      },
      onScroll: (info) => {
        const distanceFromEnd = info.scrollHeight - info.top -
          $scroll.offsetHeight;
        const scrollingUp = info.top < this.lastTop;
        this.lastTop = info.top;
        this.following = scrollingUp
          ? false
          : distanceFromEnd < AgentTranscript.FOLLOW_SLACK_PX;
      },
      options: {
        cellWidthForIndex: (index) => this.measured.get(index) ?? null,
      },
    });

    this.$root.append($scroll);
    void $scroll.bind({
      cellHeightPx: this.estimatedRowHeight,
      maxIndex: Math.max(0, this.count - 1),
      minIndex: 0,
    }).then(() => {
      this.ready = true;
      // Rows measured before the viewport was ready are still flagged
      // unmeasured (the engine hides those); one invalidation applies every
      // height captured during that window.
      $scroll.invalidateHeight(0, false);
      this.scrollToEnd();
    });
  }

  private report($cell: HTMLElement, height: number): void {
    // A recycled cell still resolves in `cellIndex` — to the row it last
    // held — and its emptied wrap still has a height (its padding), so a
    // report from it would overwrite a real measurement with garbage. The
    // attribute is `std-scroll-vertical`'s recycle mark, set before this
    // host's onRecycle runs and cleared again on bind.
    if ($cell.hasAttribute("recycled")) return;
    const index = this.cellIndex.get($cell);
    if (index == null || height === 0 || this.measured.get(index) === height) {
      return;
    }
    this.measured.set(index, height);
    $cell.style.height = `${height}px`;
    if (!this.ready) return;
    // Anchoring keeps the reader's row still while heights settle above;
    // while following the tail we want the opposite — stay pinned to it.
    this.$scroll?.invalidateHeight(index, !this.following);
    if (this.following) this.scrollToEnd();
  }

  onRecycle(): void {
    super.onRecycle();
    // Observer teardown lives HERE, not in disconnectedCallback: a cached
    // transcript is detached and reattached without recycling, keeps its
    // $scroll (mount() is skipped), and a teardown on detach would leave it
    // permanently deaf to row resizes — stored heights and virtualization
    // offsets would drift stale. Pooling is the real end of life.
    this.observer?.disconnect();
    this.observer = null;
    this.$scroll = null;
    this.renderAt = null;
    this.count = 0;
    this.revision = null;
    this.measured.clear();
    this.following = true;
    this.lastTop = 0;
    this.ready = false;
    this.$root.replaceChildren();
  }

  static $styles = [
    css`
      /* THE TURN TYPE SCALE — and it is the app's, not one of its own.
         Revised on review: "we need to better align the typography and
         font-sizes overall. they're very inconsistent. unify them into a
         couple of sizes", then, when the first attempt invented a scale
         beside the app's: "we are NOT permitted to use font-size. we MUST
         use --font-size and lona typography."

         The first attempt is worth recording because it was wrong in a way
         that looked right. It minted --turn-text-lead / --turn-text /
         --turn-text-fine as PIXEL values and set them with the font-size
         property on bare spans, reasoning that a span matches no ramp
         selector so --font-size would be inert on it. That is true and it
         is the bug, not the excuse: the fix is to stop using bare spans.
         It also silently broke the sizes it set — Typography computes
         --rfont-size as --font-size / (1 - --cap-height - --baseline),
         so --font-size is a CAP-HEIGHT BOX and a pixel value handed to it
         renders 1/0.72 larger. Measured: a title asking for 14px rendered
         at 19.44px, which is what "typography too big" was filed against,
         twice.

         Both problems have the same one-line fix, which is to use what
         every other component in this repo uses. Text is a ramp element
         (<p class="small">, the shape entry-row and the card header are
         built from), sized with --font-size set to a semantic ALIAS. An
         alias is already in the ramp's unit, so it cannot be scaled wrong,
         and it tracks whatever spacing set the app is running.

         So the family's three registers ARE three aliases, and there is no
         fourth number anywhere:

           --p-size        text the reader READS — the reply's prose, the
                           user's prompt, an ask's question, the live line
           --small-size    the words of a ROW — labels, subjects, values,
                           summaries, filenames
           --caption-size  facts ABOUT a row, and verbatim panels — counts,
                           exit codes, key/value grids, code

         Nothing is declared here, deliberately. A scale that needed a host
         to declare it would leave every primitive unsized in a storybook
         card; aliases resolve from the theme wherever the component is
         mounted. What this host still owns is the rhythm BETWEEN rows,
         below, which is a layout number and not type. */
      :host {
        display: block;
        /* NOT a type size: the box the chevron and the status mark are
           centred in, which is layout and stays a number. */
        --turn-line: 20px;
        /* Rows are absolutely positioned, so the host has NO intrinsic
           width — without this it collapses to zero inside any container
           that sizes children to content (a centered storybook card, a
           flex row), and every row wraps to a single character. */
        width: 100%;
        height: 100%;
        /* A transcript is read material — never a click surface. */
        user-select: text;
        cursor: auto;
      }
      #root {
        width: 100%;
        height: 100%;
      }
      std-scroll-vertical {
        width: 100%;
        height: 100%;
        --scroll-padding-top: var(--transcript-padding-top, 12px);
        --scroll-padding-bottom: var(--transcript-padding-bottom, 12px);
      }
      .cell {
        overflow: hidden;
      }
      /* The gap between rows lives HERE, as padding on the measured
         wrapper, not as a margin on the rows. Revised on review: "need
         better spacing between elements."

         The transcript had none at all: every primitive drew itself flush
         and the host stacked them at their natural heights, so a plan, the
         tool run under it and the answer after that were one unbroken block
         of text with no seam to tell a reader where one thing ended.

         Padding rather than margin because this host MEASURES. Row heights
         come from the wrapper's offsetHeight and feed the scroll engine, and
         offsetHeight does not see a margin — a margin would have spaced the
         rows visually while every cached height stayed a gap too short, and
         the error accumulates down the scrollback. Padding is inside the
         box being measured, so the number the engine gets is the number the
         reader sees.

         24px, and this is the third figure. Revised on review twice: "need
         better spacing between elements", then "need more space between
         turns" against the 8px that answered it, then again against 16px.

         A row here is a whole TURN — a plan, the run of calls under it, the
         answer — so the gap is not spacing, it is the only thing marking
         where one turn ends. At 8px it was the same size as the seam between
         two rows INSIDE a turn, which is to say it was not a boundary at
         all. 16px cleared the row pitch but only just: a call row is 20px,
         so the turn seam was still SMALLER than the thing it separated, and
         a reader counts that as one more row.

         24px is the first figure that reads as a boundary rather than as a
         gap — comfortably past the 20px pitch it has to beat, and the same
         order as the space the primitives leave inside themselves. Judged
         against a measured pitch rather than picked: if the call row's
         padding changes, this is the number that has to move with it. */
      .wrap {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding-bottom: var(--transcript-row-gap, 24px);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="root"></div>
  `;
}

export namespace AgentTranscript {
  /** First-paint guess, before any row has been measured. */
  export const DEFAULT_ROW_HEIGHT = 28;
  /** How close to the end still counts as "following the tail". */
  export const FOLLOW_SLACK_PX = 100;

  export type Props = {
    count: number;
    /** Builds the row at `index`. Called on mount and on recycle. */
    renderAt: (index: number) => HTMLElement;
    /** Changes when row content changes without changing the row count. */
    revision?: Option<string>;
    estimatedRowHeight?: number;
  };
}
